import {
  Annotation,
  AnnotationSummary,
  CreateAnnotationPayload,
  ReportsOverview,
  ToolCall,
  TraceDetailResponse,
  TraceListResponse,
  TraceStep
} from "./types";

const API_BASE = import.meta.env.VITE_API_BASE?.replace(/\/$/, "") ?? "";
const INGEST_URL = import.meta.env.VITE_INGEST_URL?.replace(/\/$/, "");

async function request<T>(path: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      ...(options.headers ?? {})
    }
  });
  if (!response.ok) {
    const errorText = await safeReadText(response);
    throw createApiError(response.status, errorText);
  }

  const bodyText = await safeReadText(response);
  if (!bodyText) {
    return undefined as T;
  }

  const contentType = response.headers.get("content-type") ?? "";
  if (contentType.includes("application/json")) {
    return JSON.parse(bodyText) as T;
  }

  return bodyText as unknown as T;
}

async function safeReadText(response: Response): Promise<string> {
  // Some endpoints legitimately return no content (204/205) or an empty body.
  if (response.status === 204 || response.status === 205) {
    return "";
  }

  // Reading the body might throw if there is nothing to consume; swallow that and fall back to empty string.
  try {
    return await response.text();
  } catch (error) {
    console.warn("Failed to read response body", error);
    return "";
  }
}

function createApiError(status: number, body: string): Error {
  const { message, retryAfterMs } = formatApiErrorMessage(status, body);
  const error = new Error(message);
  (error as any).status = status;
  if (retryAfterMs !== undefined) {
    (error as any).retryAfterMs = retryAfterMs;
  }
  return error;
}

function formatApiErrorMessage(status: number, body: string): { message: string; retryAfterMs?: number } {
  let message = body;
  let retryAfterMs: number | undefined;

  try {
    const parsed = JSON.parse(body);
    if (typeof parsed?.error === "string") {
      message = parsed.error;
    } else if (typeof parsed?.message === "string") {
      message = parsed.message;
    }
    if (typeof parsed?.retryAfterMs === "number") {
      retryAfterMs = parsed.retryAfterMs;
    }
  } catch (error) {
    // ignore JSON parse errors; fall back to raw body
  }

  message = message.replace(/\s+/g, " ").trim();

  if (!message) {
    switch (status) {
      case 429:
        message = "Rate limited by OpenAI. Please wait and try again.";
        break;
      case 502:
        message = "OpenAI returned an empty response. Try again shortly.";
        break;
      default:
        message = `Request failed with status ${status}.`;
    }
  } else {
    if (status === 429 && !/rate/i.test(message)) {
      message = `Rate limited by OpenAI. ${message}`;
    }
    if (status === 502 && /empty response/i.test(message)) {
      message = "OpenAI returned an empty response. Try again shortly.";
    }
  }

  if (retryAfterMs !== undefined && retryAfterMs > 0) {
    const seconds = Math.max(1, Math.ceil(retryAfterMs / 1000));
    message = `${message} (retry in ~${seconds}s)`;
  }

  if (message.length > 260) {
    message = `${message.slice(0, 257)}…`;
  }

  return { message, retryAfterMs };
}

export function fetchTraces(params: {
  cursor?: string;
  limit?: number;
  severity?: string;
  failureMode?: string;
  startDate?: string;
  endDate?: string;
  channel?: string;
  reviewStatus?: string;
}): Promise<TraceListResponse> {
  const query = new URLSearchParams();
  if (params.cursor) query.set("cursor", params.cursor);
  if (params.limit) query.set("limit", String(params.limit));
  if (params.severity) query.set("severity", params.severity);
  if (params.failureMode) query.set("failureMode", params.failureMode);
  if (params.startDate) query.set("startDate", params.startDate);
  if (params.endDate) query.set("endDate", params.endDate);
  if (params.channel) query.set("channel", params.channel);
  if (params.reviewStatus && params.reviewStatus !== "today") {
    query.set("reviewStatus", params.reviewStatus);
  }
  const q = query.toString();
  return request<TraceListResponse>(`/api/traces${q ? `?${q}` : ""}`);
}

export async function exportAnnotationsRequest(
  params: {
    severity?: string;
    failureMode?: string;
    startDate?: string;
    endDate?: string;
    channel?: string;
    reviewStatus?: string;
  },
  format: "csv" | "json" = "csv"
): Promise<{ blob: Blob; filename: string; contentType: string }> {
  const query = new URLSearchParams();
  if (params.severity) query.set("severity", params.severity);
  if (params.failureMode) query.set("failureMode", params.failureMode);
  if (params.startDate) query.set("startDate", params.startDate);
  if (params.endDate) query.set("endDate", params.endDate);
  if (params.channel) query.set("channel", params.channel);
  if (params.reviewStatus && params.reviewStatus !== "today") {
    query.set("reviewStatus", params.reviewStatus);
  }
  query.set("format", format);

  const response = await fetch(`${API_BASE}/api/annotations/export?${query.toString()}`, {
    method: "GET"
  });

  if (!response.ok) {
    const errorText = await safeReadText(response);
    throw new Error(errorText || `Export failed with status ${response.status}`);
  }

  const blob = await response.blob();
  const disposition = response.headers.get("content-disposition") ?? "";
  const filename = parseFilenameFromDisposition(disposition) ?? `annotations-export.${format}`;
  const contentType = response.headers.get("content-type") ?? "application/octet-stream";

  return { blob, filename, contentType };
}

export async function fetchTrace(id: string): Promise<TraceDetailResponse> {
  const data = await request<TraceDetailResponse>(`/api/traces/${id}`);
  return {
    ...data,
    steps: data.steps.map(normalizeStep),
    toolCalls: data.toolCalls.map(normalizeToolCall),
    annotations: data.annotations.map(normalizeAnnotation)
  };
}

export function listFailureModes(): Promise<{ items: string[] }> {
  return request<{ items: string[] }>(`/api/failure-modes`);
}

export function listChannels(): Promise<{ items: string[] }> {
  return request<{ items: string[] }>(`/api/channels`);
}

export async function createAnnotationApi(payload: CreateAnnotationPayload): Promise<Annotation> {
  const data = await request<Annotation>(`/api/annotations`, {
    method: "POST",
    body: JSON.stringify(payload)
  });
  return normalizeAnnotation(data);
}

export function deleteTrace(id: string): Promise<void> {
  return request<void>(`/api/traces/${id}`, {
    method: "DELETE"
  });
}

export async function updateAnnotationApi(id: number, payload: Partial<Annotation>): Promise<Annotation> {
  const data = await request<Annotation>(`/api/annotations/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload)
  });
  return normalizeAnnotation(data);
}

export function deleteAnnotationApi(id: number): Promise<void> {
  return request<void>(`/api/annotations/${id}`, {
    method: "DELETE"
  });
}

export async function triggerIngest(): Promise<boolean> {
  if (!INGEST_URL) {
    return false;
  }
  try {
    const res = await fetch(`${INGEST_URL}/ingest`, {
      method: "POST",
      headers: { "Content-Type": "application/json" }
    });
    return res.ok;
  } catch (error) {
    console.error("Failed to trigger ingest", error);
    return false;
  }
}

export function fetchAnnotationSummary(): Promise<AnnotationSummary[]> {
  return request<{ items: AnnotationSummary[] }>(`/api/annotation-summary`).then((res) => res.items);
}

export function fetchReportsOverview(): Promise<ReportsOverview> {
  return request<ReportsOverview>(`/api/reports/overview`);
}

function normalizeAnnotation(raw: Annotation): Annotation {
  return {
    ...raw,
    traceId: raw.traceId ?? (raw as any).trace_id,
    createdAt: raw.createdAt ?? (raw as any).created_at,
    failureMode: raw.failureMode ?? (raw as any).failure_mode,
    resolutionNote: raw.resolutionNote ?? (raw as any).resolution_note
  };
}

function normalizeStep(raw: TraceStep | Record<string, unknown>): TraceStep {
  const stepIndexValue = (raw as any).stepIndex ?? (raw as any).step_index;
  const stepIndex = typeof stepIndexValue === "number" ? stepIndexValue : Number(stepIndexValue ?? 0);
  const traceId = (raw as any).traceId ?? (raw as any).trace_id ?? "";

  return {
    traceId,
    stepIndex: Number.isFinite(stepIndex) ? stepIndex : 0,
    role: (raw as any).role ?? undefined,
    content: (raw as any).content ?? undefined
  };
}

function normalizeToolCall(raw: ToolCall | Record<string, unknown>): ToolCall {
  const successRaw = (raw as any).success;
  const stepIndexValue = (raw as any).stepIndex ?? (raw as any).step_index;
  const stepIndex = typeof stepIndexValue === "number" ? stepIndexValue : Number(stepIndexValue ?? 0);
  const traceId = (raw as any).traceId ?? (raw as any).trace_id ?? "";
  const argumentsSource = (raw as any).argumentsJson ?? (raw as any).arguments_json;
  const resultSource = (raw as any).resultJson ?? (raw as any).result_json;
  let success: boolean | undefined;
  if (typeof successRaw === "boolean") {
    success = successRaw;
  } else if (typeof successRaw === "number") {
    success = successRaw === 1 ? true : successRaw === 0 ? false : undefined;
  } else if (typeof successRaw === "string") {
    if (successRaw.toLowerCase() === "true") success = true;
    else if (successRaw.toLowerCase() === "false") success = false;
  }

  return {
    traceId,
    stepIndex: Number.isFinite(stepIndex) ? stepIndex : 0,
    toolName: (raw as any).toolName ?? (raw as any).tool_name ?? undefined,
    argumentsJson: safeJsonString(argumentsSource),
    resultJson: safeJsonString(resultSource),
    success,
    errorMessage: (raw as any).errorMessage ?? (raw as any).error_message ?? undefined
  };
}

function safeJsonString(value: unknown): string | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (typeof value === "string") {
    return value;
  }
  try {
    return JSON.stringify(value);
  } catch (error) {
    console.warn("Failed to stringify tool call payload", error);
    return undefined;
  }
}

function parseFilenameFromDisposition(disposition: string): string | undefined {
  const match = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(disposition ?? "");
  if (!match) return undefined;
  const encoded = match[1] ?? match[2];
  if (!encoded) return undefined;
  try {
    return decodeURIComponent(encoded);
  } catch (_) {
    return encoded;
  }
}
